
import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from "react";
import styles from './BusinessStep.module.css';
import { trackEvent } from "../../lib/analytics";
import { isIndiaSite, isPythonBackend } from "../../utils/apiBackend";
import { buildGreetingVariants } from "../../utils/greetingVariants";
import detectPlatform from "../../utils/detectPlatform";

// Keyboard wala scroll sirf native app (Android/iOS WebView) ke liye hai.
// Browser me focus par browser khud element ko view me le aata hai.
const isMobileApp = () => {
    const platform = detectPlatform();
    return platform === "android" || platform === "ios";
};

// Edit flow (EditNameAvtar/Step3) bhi 300 par cap karta hai — dono jagah same.
const GREETING_MAX_LENGTH = 300;

const allLanguages = [
    /* English family */
    {
        name: "English",
        locale: "en-US",
    },
    // {
    //     name: "English (India)",
    //     locale: "en-IN",
    // },
    // {
    //     name: "English (UK)",
    //     locale: "en-GB",
    // },
    // {
    //     name: "English (Australia)",
    //     locale: "en-AU",
    // },
    // {
    //     name: "English (New Zealand)",
    //     locale: "en-NZ",
    // },

    /* Germanic & Nordic */
    {
        name: "German",
        locale: "de-DE",
    },
    {
        name: "Dutch",
        locale: "nl-NL",
    },
    {
        name: "Danish",
        locale: "da-DK",
    },
    {
        name: "Finnish",
        locale: "fi-FI",
    },
    {
        name: "Norwegian",
        locale: "no-NO",
    },
    {
        name: "Swedish",
        locale: "sv-SE",
    },

    /* Romance */
    {
        name: "Spanish (Spain)",
        locale: "es-ES",
    },
    {
        name: "Spanish (LatAm)",
        locale: "es-419",
    },
    {
        name: "French (France)",
        locale: "fr-FR",
    },
    {
        name: "French (Canada)",
        locale: "fr-CA",
    },
    {
        name: "Italian",
        locale: "it-IT",
    },
    {
        name: "Portuguese (Portugal)",
        locale: "pt-PT",
    },
    {
        name: "Portuguese (Brazil)",
        locale: "pt-BR",
    },
    {
        name: "Catalan",
        locale: "ca-ES",
    },
    {
        name: "Romanian",
        locale: "ro-RO",
    },

    /* Slavic & Baltic */
    {
        name: "Polish",
        locale: "pl-PL",
    },
    {
        name: "Russian",
        locale: "ru-RU",
    },
    {
        name: "Bulgarian",
        locale: "bg-BG",
    },
    {
        name: "Slovak",
        locale: "sk-SK",
    },

    /* Hellenic & Uralic */
    {
        name: "Greek",
        locale: "el-GR",
    },
    {
        name: "Hungarian",
        locale: "hu-HU",
    },

    /* Asian */
    {
        name: "Hindi",
        locale: "hi-IN",
    },
    {
        name: "Japanese",
        locale: "ja-JP",
    },
    {
        name: "Korean",
        locale: "ko-KR",
    },
    {
        name: "Chinese (Mandarin)",
        locale: "zh-CN",
    },
    {
        name: "Vietnamese",
        locale: "vi-VN",
    },
    {
        name: "Indonesian",
        locale: "id-ID",
    },

    /* Turkic */
    {
        name: "Turkish",
        locale: "tr-TR",
    },
    {
        name: "Arabic",
        locale: "ar-SA",
    }
];

// Python backend par English ke 5 accent variants ki jagah ek hi "English".
//
// Fish Audio (Python ka TTS) me accent koi setting nahi hai: har voice ek fixed
// reference id hai aur accent usi recording ke andar hota hai. Ek hi voice id
// set hai, isliye "English (UK)" aur "English (India)" bilkul same awaaz dete —
// user ko 5 option dikhte jo asar kuch na karte. Baaki saari languages waisi hi
// rehti hain; jab per-accent voice ids aa jayein to yahan wapas jod dena.
const ENGLISH_VARIANTS = new Set(["en-US", "en-IN", "en-GB", "en-AU", "en-NZ"]);

const pythonLanguages = [
    { name: "English", locale: "en-US" },
    ...allLanguages.filter(l => !ENGLISH_VARIANTS.has(l.locale)),
];

// India (.in) is Sarvam only. These two locales are the ones Sarvam speaks that
// this product supports: it speaks eleven, but our STT (Deepgram Flux)
// understands just Hindi and English of them — offering Tamil or Bengali would
// give an agent that talks but cannot listen.
//
// Fish is not offered here on purpose. AppSumo users reach the Python backend
// through their UTM rather than through this domain, so `isIndiaSite()` is
// false for them and they get the ordinary Fish list.
//
// Hindi first, because the first entry becomes the default.
const indiaLanguages = [
    { name: "Hindi", locale: "hi-IN" },
    { name: "English (India)", locale: "en-IN" },
];

export const getLanguages = () => {
    if (isIndiaSite()) return indiaLanguages;
    return isPythonBackend() ? pythonLanguages : allLanguages;
};

/**
 * The language an agent starts with before the user picks one.
 *
 * Exported because the wizard sets this default in three places of its own
 * (initial state and two resets) and used to hardcode "en-US" in each. On the
 * India site that produced an English agent on Fish whenever the user never
 * opened the language step — which is most of them, since the step defaults to
 * whatever is already selected.
 */
export const defaultLanguage = () => getLanguages()[0];

const getFlag = (locale) => {
    if (locale === "multi") return "/images/multi.png";
    if (locale === "es-419") return "https://flagcdn.com/w80/es.png";
    return `https://flagcdn.com/w80/${locale?.split("-")[1]?.toLowerCase()}.png`;
};

const StepThree = forwardRef(({ data, updateData }, ref) => {
    const [open, setOpen] = useState(false);
    
    //  FIX: Initialize with defaults IMMEDIATELY to prevent flickering
    // Python par en-GB / en-IN jaise accent locales list me nahi hote, to
    // purane agent ka code match nahi karega — `|| [0]` fallback usse chup-chaap
    // "English" par le aata hai. Baaki languages (Danish, Hindi...) jaisi thi
    // waisi hi milti hain.
    const [selected, setSelected] = useState(() => {
        const list = getLanguages();
        if (data?.agentLanguageCode) {
            const lang = list.find(l => l.locale === data.agentLanguageCode);
            return lang || list[0];
        }
        return list[0];
    });
    
    const [activeRole, setActiveRole] = useState(() => {
        return data?.agentRole || "General Receptionist";
    });

    /*
     * GREETING
     * Role cards ki jagah ab yahi block hai. Do hi options hain:
     *   "default" — koi line pin nahi. customGreeting khaali jaati hai aur
     *               backend ka getGreetingSection() (promptHelpers.js) prompt me
     *               teen natural variations daal deta hai, jinme business aur
     *               agent ka naam bhar jaata hai. Ye hamesha se ka behaviour hai.
     *   "custom"  — user ki apni line, jo prompt me "use this exact greeting"
     *               block ban kar jaati hai.
     * Role ab UI se hat gaya par payload me default jaata rehta hai.
     */
    const [greetingChoice, setGreetingChoice] = useState(() =>
        data?.greetingChoice === "custom" &&
            String(data?.customGreetingText || "").trim()
            ? "custom"
            : "default"
    );
    const [customGreetingText, setCustomGreetingText] = useState(
        () => data?.customGreetingText || ""
    );
    const customGreetingBoxRef = useRef(null);
    const greetingTextareaRef = useRef(null);

    // Sirf Default card me dikhane ke liye — jo actually prompt me jaata hai wo
    // backend khud banata hai (hum "Default" par customGreeting khaali bhejte hain).
    const greetingVariants = buildGreetingVariants(
        data?.placeDetails?.businessName || data?.displayBusinessName,
        data?.agentName,
        // Pounce onboarding (BusinessStep ye flag rakhta hai) → Pounce ka opener.
        sessionStorage.getItem("onboardingFlow"),
        {
            languageCode: selected.locale,
            isPythonBackend: isPythonBackend(),
        }
    );

    // Custom khaali chhoda to bhi khaali hi jaata hai — matlab default greeting.
    const resolvedGreeting =
        greetingChoice === "custom" ? customGreetingText.trim() : "";

    //  FIX: Single initialization effect - no extra re-renders
    useEffect(() => {
        // Only update if data changes and differs from current state
        if (data) {
            let shouldUpdate = false;
            let updates = {};

            // Check language change
            if (data.agentLanguageCode && data.agentLanguageCode !== selected.locale) {
                const lang = getLanguages().find(l => l.locale === data.agentLanguageCode);
                if (lang && lang.locale !== selected.locale) {
                    updates.selected = lang;
                    shouldUpdate = true;
                }
            }

            // Check role change
            if (data.agentRole && data.agentRole !== activeRole) {
                updates.activeRole = data.agentRole;
                shouldUpdate = true;
            }

            // 🆕 FIX: Single batch update to prevent flickering
            if (shouldUpdate) {
                if (updates.selected) setSelected(updates.selected);
                if (updates.activeRole) setActiveRole(updates.activeRole);
            }
        }
    }, [data?.agentLanguageCode, data?.agentRole]); // Only watch specific props

    // Update parent only when user changes (not on load)
    const handleLanguageChange = (lang) => {
        setSelected(lang);
        setOpen(false);
        // GA4: user picked the AI's language from the dropdown.
        trackEvent("ai_language_selected", {
            language: lang.name,
            language_code: lang.locale,
        });
        updateData({
            agentLanguage: lang.name,
            agentLanguageCode: lang.locale,
        });
    };

    const handleGreetingChoice = (choice) => {
        setGreetingChoice(choice);
        // GA4: user picked the AI's opening line.
        trackEvent("ai_greeting_selected", { greeting_type: choice });
        updateData({ greetingChoice: choice });
        /*
         * Custom card list me sabse neeche hai — click karte hi textarea me focus
         * de dete hain. `preventScroll` NAHI dena: mobile par focus se keyboard
         * khulta hai aur browser khud element ko view me laata hai; preventScroll
         * usi ko rok deta tha, isliye box keyboard ke peeche chhup jaata tha.
         * Keyboard ke baad ka final adjustment onFocus handler karta hai.
         */
        if (choice === "custom") {
            setTimeout(() => greetingTextareaRef.current?.focus(), 60);
        }
    };

    // Ek hi jagah se scroll, taaki behaviour dono jagah same rahe.
    const scrollCustomGreetingIntoView = () => {
        customGreetingBoxRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
    };

    /*
     * Keyboard khulte hi visual viewport chhota ho jaata hai. Focus ke turant
     * baad scroll karenge to purani (badi) height ke hisaab se position bithayenge
     * aur box keyboard ke peeche chala jaayega. Isliye viewport ke resize hone ka
     * intezaar karte hain; resize na aaye (desktop, ya purane browser) to timeout
     * fallback se scroll kar dete hain.
     */
    const handleCustomGreetingFocus = () => {
        // Desktop/mobile browser par keyboard hi nahi uthta (ya browser khud
        // handle kar leta hai) — ye correction sirf native app ke liye chahiye.
        if (!isMobileApp()) return;

        const vv = window.visualViewport;
        if (!vv) {
            setTimeout(scrollCustomGreetingIntoView, 300);
            return;
        }
        const onViewportResize = () => {
            clearTimeout(fallbackTimer);
            vv.removeEventListener("resize", onViewportResize);
            scrollCustomGreetingIntoView();
        };
        const fallbackTimer = setTimeout(() => {
            vv.removeEventListener("resize", onViewportResize);
            scrollCustomGreetingIntoView();
        }, 350);
        vv.addEventListener("resize", onViewportResize);
    };

    const handleCustomGreetingChange = (e) => {
        const value = e.target.value.slice(0, GREETING_MAX_LENGTH);
        setCustomGreetingText(value);
        updateData({ customGreetingText: value });
    };

    // Jo screen pe selected hai wahi formData.customGreeting me rehta hai, taaki
    // buildFinalPayload bina dobara resolve kiye seedha bhej sake. Business/agent
    // name badalne par pre-built line apne aap refresh ho jaati hai.
    useEffect(() => {
        // `|| ""` dono taraf zaroori hai: Default par resolvedGreeting khaali
        // hota hai, aur khaali value updateFormData secureStorage se hata deti
        // hai (formData me undefined ban jaata hai) — bina normalize kiye ye
        // comparison hamesha true rehta aur effect loop me chala jaata.
        if (resolvedGreeting !== (data?.customGreeting || "")) {
            updateData({ customGreeting: resolvedGreeting });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedGreeting, data?.customGreeting]);

    // Create final payload for agent creation
    const createPayload = () => {
        const payload = {
            // Step 1 Data
            businessType: data.businessType || "",
            customBusiness: data.customBusiness || "",

            // Step 2 Data (GMB or Manual)
            displayBusinessName: data.displayBusinessName || "",
            googleListing: data.googleListing || "",
            noGoogleListing: data.noGoogleListing || false,
            businessUrl: data.businessUrl || "",
            noBusinessWebsite: data.noBusinessWebsite || false,
            
            // GMB Details
            placeDetails: {
                businessName: data.placeDetails?.businessName || data.displayBusinessName || "",
                phone: data.placeDetails?.phone || "",
                internationalPhone: data.placeDetails?.internationalPhone || "",
                address: data.placeDetails?.address || "",
                email: data.placeDetails?.email || "",
                website: data.placeDetails?.website || data.businessUrl || "",
                aboutBusiness: data.placeDetails?.aboutBusiness || "",
                state: data.placeDetails?.state || "",
                city: data.placeDetails?.city || "",
                country: data.placeDetails?.country || "",
                postalCode: data.placeDetails?.postalCode || "",
            },

            // Sitemap URLs
            sitemapUrls: data.sitemapUrls || [],
            selectedSitemapUrls: data.selectedSitemapUrls || [],

            // Step 3 Data
            agentLanguage: selected.name,
            agentLanguageCode: selected.locale,
            agentRole: activeRole,
            customGreeting: resolvedGreeting,
            greetingChoice,
            customGreetingText,

            // Metadata
            timestamp: new Date().toISOString(),
            flowType: 'simple'
        };

        return payload;
    };

    // Expose validate method
    useImperativeHandle(ref, () => ({
        validate: () => {
            // Language and role always valid (have defaults)
            return true;
        },
        getPayload: () => {
            return createPayload();
        }
    }));


    return (
        <>
            <div className={styles.titleBar}>
                <h4 className={styles.lightTitle}>What should your</h4>
                <h5 className={styles.boldTitle}>receptionist do?</h5>
                <p className={styles.subtitle}>
                   Choose its primary role. <br/> You can change this later.
                </p>
            </div>

            <div className={styles.mainWrap}>
                {/* LANGUAGE BLOCK */}
                <div className={styles.block}>
                    <h3 className={styles.heading}>Language</h3>

                    <div 
                        className={styles.languageRow} 
                        onClick={() => {
                            const nextState = !open;
                            setOpen(nextState);
                            if (nextState) trackEvent("language_dropdown_opened", { screen: "receptionist_role" });
                        }}
                    >
                        <img src={getFlag(selected.locale)} className={styles.flag} alt="" />
                        <span className={styles.languageLabel}>{selected.name}</span>
                        <img
                            src="/svg/down-arrow.svg"
                            className={`${styles.dropdownIcon} ${open ? styles.rotate : ""}`}
                            alt="down-arrow.svg"
                        />
                    </div>

                    {open && ( 
                        <div className={styles.options}>
                            {getLanguages().map((lang, index) => (
                                <div
                                    key={index}
                                    className={styles.option}
                                    onClick={() => handleLanguageChange(lang)}
                                >
                                    <img src={getFlag(lang.locale)} className={styles.flag} alt="" />
                                    <span>{lang.name}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* GREETING BLOCK */}
                <div className={styles.block}>
                    <h3 className={styles.heading}>Greetings</h3>

                    <div className={styles.roleWrapper}>
                        <div
                            className={`${styles.roleBox} ${greetingChoice === "default" ? styles.selected : ""}`}
                            onClick={() => handleGreetingChoice("default")}
                        >
                            <div className={styles.roleTextBlock}>
                                <p className={styles.roleTitle}>
                                    Default{" "}
                                    {greetingVariants.length > 1 && (
                                        <span className={styles.greetingDefaultHint}>
                                            (1 of these)
                                        </span>
                                    )}
                                </p>
                                <ul className={styles.greetingVariantList}>
                                    {greetingVariants.map((variant, index) => (
                                        <li key={index} className={styles.greetingVariantItem}>
                                            {variant}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <span className={styles.selectCircle}></span>
                        </div>

                        <div
                            className={`${styles.roleBox} ${greetingChoice === "custom" ? styles.selected : ""}`}
                            onClick={() => handleGreetingChoice("custom")}
                        >
                            <div className={styles.roleTextBlock}>
                                <p className={styles.roleTitle}>Custom greeting</p>
                                <p className={styles.roleSubText}>
                                    Write your own opening line
                                </p>
                            </div>
                            <span className={styles.selectCircle}></span>
                        </div>

                        {greetingChoice === "custom" && (
                            <div className={styles.greetingCustomBox} ref={customGreetingBoxRef}>
                                <textarea
                                    ref={greetingTextareaRef}
                                    className={styles.greetingTextarea}
                                    value={customGreetingText}
                                    onChange={handleCustomGreetingChange}
                                    onFocus={handleCustomGreetingFocus}
                                    maxLength={GREETING_MAX_LENGTH}
                                    placeholder="Enter a custom greeting for your agent..."
                                />
                                <div className={styles.greetingFootRow}>
                                    <span className={styles.roldesc}>
                                        Leave it blank to use the default greeting.
                                    </span>
                                    <span className={styles.greetingCharCount}>
                                        {customGreetingText.length}/{GREETING_MAX_LENGTH}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
});

export default StepThree;
